import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  CreditCard, Building2, Receipt, Crown, Check,
  Clock, AlertCircle, Download, ExternalLink, Loader2,
  DollarSign, Calendar, FileText, ChevronRight
} from 'lucide-react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const BillingPage = () => {
  const navigate = useNavigate();
  const {
    currentOrg,
    user,
    isAdmin,
    canPerform
  } = useOutletContext();

  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [history, setHistory] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  useEffect(() => {
    if (currentOrg && !canPerform('SETTINGS_BILLING') && !isAdmin) {
      toast.error('Only organization owners and billing admins can access this page');
      navigate('/dashboard');
    }
  }, [currentOrg, canPerform, isAdmin, navigate]);

  useEffect(() => {
    if (currentOrg) {
      fetchBillingData();
    }
  }, [currentOrg]);

  const fetchBillingData = async () => {
    if (!currentOrg) return;

    setLoading(true);
    try {
      // Build overview from context data
      const overviewData = {
        has_subscription: currentOrg.subscription_status === 'pro' || currentOrg.subscription_tier === 'pro',
        tier: currentOrg.subscription_tier || 'demo',
        status: currentOrg.subscription_status || 'inactive',
        organisation_name: currentOrg.name,
        billing_email: user?.email,
        stripe_customer_id: currentOrg.stripe_customer_id
      };
      setOverview(overviewData);

      // Get payment history from payment_transactions
      const { data: transactions } = await supabase
        .from('payment_transactions')
        .select('*')
        .eq('organisation_id', currentOrg.id)
        .order('created_at', { ascending: false })
        .limit(20);

      const paymentHistory = (transactions || []).map(t => ({
        id: t.id,
        date: t.created_at,
        amount: t.amount,
        currency: t.currency || 'aud',
        status: t.payment_status,
        description: t.package_id || 'Subscription payment'
      }));
      setHistory(paymentHistory);

      // Invoices would need Stripe API access - show message
      setInvoices([]);
    } catch (error) {
      console.error('Error fetching billing data:', error);
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const initiateUpgrade = async (packageId) => {
    setUpgradeLoading(true);
    try {
      // Use Edge Function for checkout
      const response = await fetch(`${SUPABASE_URL}/functions/v1/stripe-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      window.location.href = data.checkout_url;
    } catch (error) {
      console.error('Error initiating upgrade:', error);
      toast.error(error.message || 'Failed to start upgrade');
      setUpgradeLoading(false);
    }
  };

  const openCustomerPortal = async () => {
    setUpgradeLoading(true);
    try {
      // Use Edge Function for portal
      const response = await fetch(`${SUPABASE_URL}/functions/v1/stripe-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          return_url: window.location.href
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create portal session');
      }

      window.location.href = data.portal_url;
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast.error(error.message || 'Failed to open subscription management');
      setUpgradeLoading(false);
    }
  };

  const downloadInvoice = async (sessionId) => {
    // Invoice download requires Stripe API - redirect to portal instead
    toast.info('Please use the Customer Portal to download invoices');
    openCustomerPortal();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#003B5C]" />
      </div>
    );
  }

  return (
    <div className="bg-[#F8F9FA] min-h-[calc(100vh-56px)]" data-testid="billing-page">
      {/* Page Header */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#003B5C] flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <CreditCard className="w-7 h-7 mr-3 text-[#74A57F]" />
              Billing & Subscription
            </h1>
            <p className="text-slate-600 mt-1">
              {currentOrg?.name} • Manage your plan and billing details
            </p>
          </div>

          {/* Manage Subscription Button */}
          {overview?.pro_organisations > 0 && (
            <Button
              onClick={openCustomerPortal}
              disabled={upgradeLoading}
              className="bg-[#003B5C] hover:bg-[#002a42] text-white"
              data-testid="manage-subscription-btn"
            >
              {upgradeLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ExternalLink className="w-4 h-4 mr-2" />
              )}
              Manage Subscription
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-500 text-sm">Total Organisations</span>
              <Building2 className="w-5 h-5 text-[#003B5C]" />
            </div>
            <p className="text-2xl font-bold text-[#003B5C]">{overview?.total_organisations || 0}</p>
          </div>

          <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-500 text-sm">Pro Subscriptions</span>
              <Crown className="w-5 h-5 text-[#74A57F]" />
            </div>
            <p className="text-2xl font-bold text-[#74A57F]">{overview?.pro_organisations || 0}</p>
          </div>

          <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-500 text-sm">Free Trial</span>
              <Clock className="w-5 h-5 text-amber-500" />
            </div>
            <p className="text-2xl font-bold text-amber-600">{overview?.free_organisations || 0}</p>
          </div>

          <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-500 text-sm">Total Spent</span>
              <DollarSign className="w-5 h-5 text-[#003B5C]" />
            </div>
            <p className="text-2xl font-bold text-[#003B5C]">
              ${overview?.total_spent?.toFixed(2) || '0.00'} <span className="text-sm font-normal text-slate-400">AUD</span>
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'overview'
              ? 'bg-[#003B5C] text-white'
              : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
          >
            <Building2 className="w-4 h-4 inline mr-2" />
            Organisations
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'history'
              ? 'bg-[#003B5C] text-white'
              : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
          >
            <Receipt className="w-4 h-4 inline mr-2" />
            Payment History
          </button>
          <button
            onClick={() => setActiveTab('invoices')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'invoices'
              ? 'bg-[#003B5C] text-white'
              : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Invoices
          </button>
        </div>

        {/* Organisations Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {overview?.organisations?.map((org) => (
              <div
                key={org.id}
                className="bg-white rounded-xl p-6 shadow-sm border border-slate-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${org.effective_subscription === 'pro' || org.subscription_status === 'pro' ? 'bg-[#74A57F]/20' : 'bg-slate-100'
                      }`}>
                      {org.effective_subscription === 'pro' || org.subscription_status === 'pro' ? (
                        <Crown className="w-6 h-6 text-[#74A57F]" />
                      ) : (
                        <Building2 className="w-6 h-6 text-slate-400" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <h3 className="font-semibold text-[#003B5C]">{org.name}</h3>
                        {org.is_primary && (
                          <span className="text-xs bg-[#003B5C]/10 text-[#003B5C] px-2 py-0.5 rounded-full">
                            Primary
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-1">
                        {org.state || 'No state set'} • {org.entry_count} entries logged
                      </p>
                      {org.subscription_start_date && !org.is_admin_provided && (
                        <p className="text-xs text-slate-400 mt-1">
                          Pro since: {new Date(org.subscription_start_date).toLocaleDateString('en-AU')}
                        </p>
                      )}
                      {/* Admin-provided access badge */}
                      {org.is_admin_provided && (
                        <div className="mt-2 inline-flex items-center px-2.5 py-1 bg-purple-50 border border-purple-200 rounded-md">
                          <svg className="w-4 h-4 text-purple-600 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                          <span className="text-xs font-medium text-purple-700">
                            Access provided by Lucent Sky ESG Admin
                          </span>
                        </div>
                      )}
                      {org.subscription_override_expiry && org.is_admin_provided && (
                        <p className="text-xs text-purple-500 mt-1">
                          Valid until: {new Date(org.subscription_override_expiry).toLocaleDateString('en-AU')}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="text-right">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${org.effective_subscription === 'pro' || org.subscription_status === 'pro'
                      ? org.is_admin_provided
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-[#74A57F]/20 text-[#74A57F]'
                      : org.subscription_status === 'cancelled'
                        ? 'bg-red-100 text-red-600'
                        : 'bg-amber-100 text-amber-700'
                      }`}>
                      {(org.effective_subscription === 'pro' || org.subscription_status === 'pro') && <Check className="w-4 h-4 mr-1" />}
                      {org.effective_subscription === 'pro' || org.subscription_status === 'pro'
                        ? org.is_admin_provided ? 'Pro (Admin)' : 'Pro'
                        : org.subscription_status === 'cancelled' ? 'Expired' : 'Free Trial'}
                    </span>

                    {org.effective_subscription !== 'pro' && org.subscription_status !== 'pro' && (
                      <div className="mt-3">
                        <Button
                          onClick={() => initiateUpgrade(org.is_primary ? 'pro_primary' : 'pro_secondary')}
                          disabled={upgradeLoading}
                          className="bg-[#74A57F] hover:bg-[#5F8575] text-white text-sm"
                        >
                          {upgradeLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Crown className="w-4 h-4 mr-1" />
                              Upgrade ${org.is_primary ? '88' : '44'}/year
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {org.effective_subscription !== 'pro' && org.subscription_status === 'free' && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-700 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                      Free trial: {org.entry_count}/{org.entry_limit} entries used. Upgrade for unlimited tracking.
                    </p>
                  </div>
                )}
              </div>
            ))}

            {overview?.organisations?.length === 0 && (
              <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-slate-200">
                <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No organisations found</p>
              </div>
            )}
          </div>
        )}

        {/* Payment History Tab */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="font-semibold text-[#003B5C]">Payment History</h3>
              <p className="text-sm text-slate-500">All payment transactions for your account</p>
            </div>

            {history.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {history.map((txn) => (
                  <div key={txn.id} className="px-6 py-4 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${txn.payment_status === 'paid'
                          ? 'bg-[#74A57F]/20'
                          : txn.payment_status === 'pending'
                            ? 'bg-amber-100'
                            : 'bg-red-100'
                          }`}>
                          {txn.payment_status === 'paid' ? (
                            <Check className="w-5 h-5 text-[#74A57F]" />
                          ) : txn.payment_status === 'pending' ? (
                            <Clock className="w-5 h-5 text-amber-600" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-red-500" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-[#003B5C]">{txn.package_name}</p>
                          <p className="text-sm text-slate-500">
                            {txn.organisation_name || 'Pending organisation'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-[#003B5C]">
                          ${txn.amount.toFixed(2)} {txn.currency}
                        </p>
                        <p className="text-xs text-slate-400">
                          {txn.created_at ? new Date(txn.created_at).toLocaleDateString('en-AU') : '-'}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${txn.payment_status === 'paid'
                          ? 'bg-[#74A57F]/20 text-[#74A57F]'
                          : txn.payment_status === 'pending'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-red-100 text-red-600'
                          }`}>
                          {txn.payment_status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-6 py-12 text-center">
                <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No payment history yet</p>
                <p className="text-sm text-slate-400 mt-1">Payments will appear here after you upgrade</p>
              </div>
            )}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="font-semibold text-[#003B5C]">Invoices & Receipts</h3>
              <p className="text-sm text-slate-500">Download receipts for your paid subscriptions</p>
            </div>

            {invoices.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {invoices.map((invoice) => (
                  <div key={invoice.invoice_number} className="px-6 py-4 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-[#003B5C]/10 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-[#003B5C]" />
                        </div>
                        <div>
                          <p className="font-medium text-[#003B5C]">{invoice.invoice_number}</p>
                          <p className="text-sm text-slate-500">{invoice.description}</p>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {invoice.organisation_name}
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex items-center space-x-4">
                        <div>
                          <p className="font-semibold text-[#003B5C]">
                            ${invoice.amount.toFixed(2)} {invoice.currency}
                          </p>
                          <p className="text-xs text-slate-400">{invoice.date_formatted}</p>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#74A57F]/20 text-[#74A57F]">
                            {invoice.status}
                          </span>
                        </div>
                        <Button
                          onClick={() => downloadInvoice(invoice.session_id)}
                          variant="outline"
                          size="sm"
                          className="border-[#003B5C] text-[#003B5C] hover:bg-[#003B5C] hover:text-white"
                          data-testid={`download-invoice-${invoice.invoice_number}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-6 py-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No invoices yet</p>
                <p className="text-sm text-slate-400 mt-1">Invoices will appear here after successful payments</p>
              </div>
            )}
          </div>
        )}

        {/* Pricing Info */}
        <div className="mt-8 bg-gradient-to-r from-[#003B5C] to-[#005580] rounded-2xl p-8 text-white">
          <h3 className="text-xl font-bold mb-4">Lucent Sky ESG Pricing</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/10 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-lg font-semibold">Demo Sandbox</span>
                <span className="text-2xl font-bold">Free</span>
              </div>
              <ul className="space-y-2 text-sm text-white/80">
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-[#74A57F]" /> 5 emissions entries</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-[#74A57F]" /> NGA factors auto-applied</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-[#74A57F]" /> Watermarked sample reports</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-[#74A57F]" /> Self-service support</li>
              </ul>
            </div>
            <div className="bg-white/10 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-lg font-semibold">Standard</span>
                <span className="text-2xl font-bold">$11<span className="text-sm font-normal">/month</span></span>
              </div>
              <ul className="space-y-2 text-sm text-white/80">
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-[#74A57F]" /> 10,000 emissions entries</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-[#74A57F]" /> 1GB evidence storage</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-[#74A57F]" /> File & photo attachments</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-[#74A57F]" /> Single-click PDF export</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-[#74A57F]" /> Standard email support</li>
              </ul>
            </div>
            <div className="bg-white/10 rounded-xl p-5 border border-[#74A57F]/50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-lg font-semibold flex items-center">
                  <Crown className="w-5 h-5 mr-2 text-[#74A57F]" />
                  Pro
                </span>
                <span className="text-2xl font-bold">$22<span className="text-sm font-normal">/month</span></span>
              </div>
              <ul className="space-y-2 text-sm text-white/80">
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-[#74A57F]" /> 100,000 emissions entries</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-[#74A57F]" /> 10GB evidence vault</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-[#74A57F]" /> OCR auto-extract from docs</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-[#74A57F]" /> Full report templates</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-[#74A57F]" /> AI Co-Pilot narratives</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-[#74A57F]" /> Priority email support</li>
              </ul>
            </div>
          </div>
          <p className="text-center text-white/60 text-sm mt-4">
            Annual billing: 10% discount • Multi-org: 20% discount on additional organisations
          </p>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
